import { LocalDateTime } from 'js-joda';

import { CalendarItem, WalkIn as WalkInCalendarItem } from 'utils/calendarItems';
import { ElaboratedTicketGroup, WalkInCalendarParty, dashboard } from 'types';
import { SingleTimeCoverAndOccupancyStats, TableOrTableGroup } from 'types/tableBasedTemplates';
import { TableAssignmentPriorityLevel } from 'generated/graphql';
import { core } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';

export type CoversStatsMap<T> = {
  [timeString: string]: T;
};

export type CoversStatsList<T> = {
  time: string;
  coversStats: T;
}[];

/**
 * Turn cover stats from a list (a shape optimized for data returned by the API)
 * into a map (a shape optimized for O(1) lookups).
 *
 * This is memoized so we can call this anywhere we need this data as a mapping
 * without incurring a cost each time it's converted.
 *
 * We might want to make this a selector if we pipe the API data into Redux,
 * but for now this gives us a convenient way to do a prototype.
 */
export const coversStatsListToMap = defaultMemoize(function <T>(
  coversStatsList: CoversStatsList<T>
): CoversStatsMap<T> {
  return coversStatsList.reduce(
    (prev: CoversStatsMap<T>, curr) => ({
      ...prev,
      [curr.time]: curr.coversStats,
    }),
    {}
  );
});

export interface TimeRange {
  startDateTime: LocalDateTime;
  anticipatedEndDateTime: LocalDateTime;
}

export interface SingleTimeCoverStats extends SingleTimeCoverAndOccupancyStats {
  offeredCovers: number;
}

// We keep this type around just for the sake of the components that use it in
// table-based-templates, but no more.
export type InventoryViewType = 'DETAILED' | 'CONDENSED';

export interface CondensedSquaresByDateTime {
  [dateTimeString: string]: CondensedCalendarSquare[];
}

export interface AdditionalInventorySection {
  calendarSquares: AdditionalInventoryByDateTime;
  sectionHeight: number;
}

export interface AdditionalInventoryByDateTime {
  [dateTimeString: string]: NormalOrCommunalSquare[];
}

export interface CalendarSlotsByDateTime {
  [dateTimeString: string]: CalendarSlot;
}

export interface CalendarSlot {
  calendarSquareIds?: string[];
  calendarSquaresById?: Dictionary<NormalDetailedSquare>;
  calendarSquares: NormalDetailedSquare[];
  inConflict: boolean;
  isOccupied: boolean;
  heldFlexSquare?: HeldCalendarSquare;
  forfeitedSquare?: ForfeitedCalendarSquare;
  availableSquares: AvailableCalendarSquare[];
}

export interface SquaresByTable {
  [tableId: string]: NormalDetailedSquare[];
}

export interface CondensedSquaresByTable {
  [tableId: string]: CondensedCalendarSquare[];
}

export type CondensedCalendarSquare = NormalCondensedCalendarSquare | WalkInCondensedCalendarSquare;

export enum CalendarSquareTypes {
  NORMAL = 'NORMAL',
}

export enum NormalSquareSubtypes {
  AVAILABLE = 'AVAILABLE',
  BOOKED = 'BOOKED',
  HELD = 'HELD',
}

interface WalkInCondensedCalendarSquare {
  id: string;
  type: 'WALK_IN';
  calendarItem: WalkInCalendarItem;
  primaryState: string;
  secondaryState?: string;
  startDateTime: LocalDateTime;
  tableId?: number;
  table?: dashboard.Table;
}

export type NormalCondensedCalendarSquare =
  | AvailableNormalCondensedSquare
  | BookedNormalCondensedCalendarSquare
  | LockedNormalCondensedCalendarSquare
  | HeldNormalCondensedCalendarSquare;

interface BaseNormalCondensedSquare {
  id: string;
  type: 'NORMAL';
  calendarItems: CalendarItem[];
  startDateTime: LocalDateTime;
  tableVariety: core.TableType.Variety;
  tableId?: number;
  table?: dashboard.Table;
  ticketGroup: ElaboratedTicketGroup;
  isBlocked: boolean;
  count?: number;
}

interface AvailableNormalCondensedSquare extends BaseNormalCondensedSquare {
  subType?: 'AVAILABLE';
}
interface BookedNormalCondensedCalendarSquare extends BaseNormalCondensedSquare {
  primaryState?: string;
  secondaryState?: string;
  subType?: 'BOOKED';
}

interface LockedNormalCondensedCalendarSquare extends BaseNormalCondensedSquare {
  subType?: 'LOCKED';
}
interface HeldNormalCondensedCalendarSquare extends BaseNormalCondensedSquare {
  subType?: 'HELD';
}

export type NormalOrCommunalSquare = NormalDetailedSquare | CommunalTicketGroup;

type CalendarSquareTurnTime = { turnTimeMinutes: number };
export type CalendarSquareTurnTimeMap = { [dateTime: string]: CalendarSquareTurnTime };

interface BaseCalendarSquare {
  anticipatedEndDateTime: LocalDateTime;
  id: string; // This is a reference to the primary calendar item's id
  row?: number;
  serviceType?: string;
  startDateTime: LocalDateTime;
  startDateTimeString: string;
  tableId?: number;
  table?: dashboard.Table;
  /**
   * on CombinedAvailableSquare this represents the turn time for the last bookable time.
   */
  turnTime?: CalendarSquareTurnTime;
  tablePriorityLevel: TableAssignmentPriorityLevel;
}

export type NormalDetailedSquare =
  | AvailableCalendarSquare
  | HeldCalendarSquare
  | LockedCalendarSquare
  | BookedCalendarSquare
  | WalkInCalendarSquare;

interface BaseNormalSquare extends BaseCalendarSquare {
  calendarItem: CalendarItem;
  type: 'NORMAL';
}

export interface AvailableCalendarItem {
  calendarItem: CalendarItem;
  isBlocked: boolean;
}

export type AvailableCalendarSquare =
  | SingleAvailableCalendarSquare
  | CombinedAvailableCalendarSquare;

interface SingleAvailableCalendarSquare extends BaseNormalSquare {
  availableCalendarItems: AvailableCalendarItem[];
  isOfferedOnline: boolean;
  priceCents: number;
  subType: 'AVAILABLE';
  ticketGroup: ElaboratedTicketGroup;
  isCombined?: false;
}

export interface CombinedAvailableCalendarSquare
  extends Omit<SingleAvailableCalendarSquare, 'isCombined'> {
  /**
   * true when the available square represents more than a single 15 minute increment
   */
  isCombined: true;
  /**
   * Expose turn times per each 15 min increment included in the calendar square
   */
  turnTimesByTime: CalendarSquareTurnTimeMap;
}

export interface HeldCalendarSquare extends BaseNormalSquare {
  isOnFlexAvailability: boolean;
  priceCents: number;
  subType: 'HELD';
  ticketSubset: core.TicketSubset;
  ticketGroup: ElaboratedTicketGroup;
}

export interface LockedCalendarSquare extends BaseNormalSquare {
  isDuplicate: boolean;
  subType: 'LOCKED';
  ticketSubset: core.TicketSubset;
  ticketGroup: ElaboratedTicketGroup;
}

// primary state and secondary state are coming from parties, and
// due to timing issues and data from different sources, they can be undefined
export interface BookedCalendarSquare extends BaseNormalSquare {
  isDuplicate: boolean;
  isOnFlexAvailability: boolean;
  primaryState?: string;
  purchase: core.PastPurchase;
  secondaryState?: string;
  subType: 'BOOKED';
  ticketGroup: ElaboratedTicketGroup;
}

export interface ForfeitedCalendarSquare {
  anticipatedEndDateTime: LocalDateTime;
  calendarItem: CalendarItem;
  id: string;
  startDateTime: LocalDateTime;
  startDateTimeString: string;
}

export interface CommunalTicketGroup extends BaseCalendarSquare {
  type: 'COMMUNAL';
  communalCalendarItem: CalendarItem;
  numAvailableTickets: number;
  numHeldTickets: number;
  numLockedTickets: number;
  numPurchases: number;
  numPurchasedTickets: number;
  subCalendarSquares: NormalDetailedSquare[];
  ticketGroup: ElaboratedTicketGroup;
}

export interface WalkInCalendarSquare extends BaseNormalSquare {
  subType: 'WALK_IN';
  calendarItem: WalkInCalendarItem;
  isDuplicate: boolean;
  party: WalkInCalendarParty;
  primaryState: string;
  secondaryState?: string;
  ticketGroup?: ElaboratedTicketGroup;
}

interface SeatingBlock {
  ticketGroupId: number;
  ticketTypeId: number;
  startDateTime: LocalDateTime;
  finishedByDateTime: LocalDateTime;
}

export interface FullyFlexedTicketGroupInfo {
  heldSubsetIds: number[];
  seatingBlocks: SeatingBlock[];
}

export interface TableWithCalendarSquares {
  isAutoReleased: boolean;
  fullyFlexedTicketGroupInfo?: FullyFlexedTicketGroupInfo;
  height: number;
  id: string;
  tableId: number;
  isArchived: boolean;
  minSize?: number;
  name: string;
  room: string;
  seatingArea?: dashboard.ISeatingArea;
  section: string;
  sectionLabel: string;
  size: number;
  slots: CalendarSlotsByDateTime;
  type: 'tableWithCalendarSquares';
}

export interface TableSection {
  label: string;
  totalCovers: number;
  type: 'section';
}

export type SectionTable = TableSection | TableWithCalendarSquares;

export type SectionsTables = SectionTable[];

export const isTableWithSquares = (item: SectionTable): item is TableWithCalendarSquares =>
  item.type === 'tableWithCalendarSquares';

export const isSection = (item: SectionTable): item is TableSection => item.type === 'section';

export interface Section<T> {
  elements: T[];
  label: string;
}

export interface TicketTypeWithColor {
  colorClassName: string;
  displayColor: string;
  name: string;
  id: number;
  shortCode: string;
}

export interface TicketTypeWithColorAndServiceTypes extends TicketTypeWithColor {
  serviceTypes: string[];
}

export type FloorplanSection = Section<TableOrTableGroup>;
